/**
 * Mixin to handle chart tooltip functionality
 * This mixin provides methods to create, manage, and cleanup chart tooltips
 */
export default {
    data() {
        return {
            isTooltipHovered: false,
            tooltipHideTimeout: null,
            tooltipShowTimeout: null,
            allowTooltipShow: true,
        };
    },
    methods: {
        /**
         * Get the tooltip element ID for this chart
         * @returns {string} The tooltip element ID
         */
        getTooltipId() {
            const sectionId = this.effectiveSectionId != null ? this.effectiveSectionId : this.id;
            return 'chart-' + this.id + '-' + sectionId + '-tooltip';
        },

        /**
         * Get or create the tooltip element
         * @returns {HTMLElement|null} The tooltip element
         */
        getOrCreateTooltipElement() {
            const tooltipId = this.getTooltipId();
            let tooltipEl = document.getElementById(tooltipId);

            if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = tooltipId;
                tooltipEl.classList.add("chart-template-tooltip");
                tooltipEl.innerHTML = '<table></table>';
                document.body.appendChild(tooltipEl);

                // Setup tooltip event listeners
                this.setupTooltipEventListeners(tooltipEl);
            }

            return tooltipEl;
        },

        /**
         * Setup event listeners for the tooltip element
         * @param {HTMLElement} tooltipEl - The tooltip element
         */
        setupTooltipEventListeners(tooltipEl) {
            const self = this;

            // Forward wheel events from tooltip to window so page can still scroll
            tooltipEl.addEventListener('wheel', (e) => {
                e.preventDefault();
                const deltaY = e.deltaY;
                if (deltaY) {
                    window.scrollBy({ top: deltaY, behavior: 'auto' });
                }
            }, { passive: false });

            // Handle mouse hover to keep tooltip visible when interacting with it
            tooltipEl.addEventListener('mouseenter', () => {
                // Clear any pending hide timeout (tooltip-level)
                if (self.tooltipHideTimeout) {
                    clearTimeout(self.tooltipHideTimeout);
                    self.tooltipHideTimeout = null;
                }
                // Clear any pending chart-level hide timeout so hoveredIndex/highlight
                // are not reset while the user is interacting with the tooltip
                if (self.hideTimeout) {
                    clearTimeout(self.hideTimeout);
                    self.hideTimeout = null;
                }
                // Clear any pending show timeout since user is now hovering
                if (self.tooltipShowTimeout) {
                    clearTimeout(self.tooltipShowTimeout);
                    self.tooltipShowTimeout = null;
                }
                self.isTooltipHovered = true;
                self.allowTooltipShow = true;
                tooltipEl.style.opacity = 1;
                tooltipEl.style.pointerEvents = 'auto';
            });

            tooltipEl.addEventListener('mouseleave', () => {
                self.isTooltipHovered = false;

                // Delay hiding by 500ms
                if (self.tooltipHideTimeout) {
                    clearTimeout(self.tooltipHideTimeout);
                }
                self.tooltipHideTimeout = setTimeout(() => {
                    if (!self.isTooltipHovered && tooltipEl) {
                        tooltipEl.style.opacity = 0;
                        tooltipEl.style.pointerEvents = 'none';

                        // Sync highlight: hide highlight when tooltip is hidden
                        if (self.hoveredIndex !== null) {
                            self.hoveredIndex = null;
                            if (self.chart) {
                                self.chart.update('none');
                                if (typeof self.animateHoverOpacity === 'function') {
                                    self.animateHoverOpacity(0, self.chart);
                                }
                            }
                        }
                    }
                    self.tooltipHideTimeout = null;
                }, 500);
            });
        },

        /**
         * Apply base styles to the tooltip element
         * @param {HTMLElement} tooltipEl - The tooltip element
         */
        applyTooltipBaseStyles(tooltipEl) {
            tooltipEl.style.opacity = 1;
            tooltipEl.style.pointerEvents = 'auto';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
            tooltipEl.style.webkitBackdropFilter = 'blur(30px) saturate(180%)';
            tooltipEl.style.backdropFilter = 'blur(30px) saturate(180%)';
            tooltipEl.style.padding = '10px 20px';
            tooltipEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            tooltipEl.style.border = '1px solid rgba(255, 255, 255, 0.3)';
            tooltipEl.style.fontFamily = "'Nunito', sans-serif";
            tooltipEl.style.transition = 'opacity 300ms ease-out';
        },

        /**
         * Position tooltip on the side: right for first half, left for second half
         * @param {HTMLElement} tooltipEl - The tooltip element
         * @param {Object} context - Chart.js tooltip context
         * @param {number} dataIndex - The data index being hovered
         */
        positionTooltipOnSide(tooltipEl, context, dataIndex) {
            const position = context.chart.canvas.getBoundingClientRect();
            const tooltipModel = context.tooltip;

            // Wait for tooltip to render to get accurate width
            const tooltipWidth = tooltipEl.offsetWidth || tooltipModel.width || 200;
            const tooltipHeight = tooltipEl.offsetHeight || tooltipModel.height || 100;

            // Calculate category width to position tooltip outside the highlighted area
            const xScale = context.chart.scales.x;
            const categoryPercentage = 0.8; // From datasets.bar.categoryPercentage
            const totalWidth = xScale.width;
            const numCategories = xScale.ticks.length;
            const categorySpacing = totalWidth / numCategories;
            const categoryWidth = categorySpacing * categoryPercentage;

            // Position tooltip outside the category area with additional spacing
            const spacing = 15; // Additional spacing beyond the category width
            const offsetFromCategoryEdge = categoryWidth / 2 + spacing;

            const totalGroups = context.chart.data.labels.length;
            const isFirstHalf = dataIndex !== null && dataIndex < totalGroups / 2;

            if (isFirstHalf) {
                // Position on the right side, outside the category area
                tooltipEl.style.left = position.left + window.scrollX + tooltipModel.caretX + offsetFromCategoryEdge + 'px';
                tooltipEl.style.top = position.top + window.scrollY + tooltipModel.caretY - tooltipHeight / 2 + 'px';
            } else {
                // Position on the left side, outside the category area
                tooltipEl.style.left = position.left + window.scrollX + tooltipModel.caretX - offsetFromCategoryEdge - tooltipWidth + 'px';
                tooltipEl.style.top = position.top + window.scrollY + tooltipModel.caretY - tooltipHeight / 2 + 'px';
            }

            // Set caret position for side positioning
            tooltipEl.classList.remove('above', 'below', 'no-transform', 'left', 'right');
            if (isFirstHalf) {
                tooltipEl.classList.add('right'); // Tooltip on right, caret on left
            } else {
                tooltipEl.classList.add('left'); // Tooltip on left, caret on right
            }
        },

        /**
         * Hide tooltips from other sections
         * @param {HTMLElement} currentTooltipEl - The current tooltip element to keep visible
         */
        hideOtherTooltips(currentTooltipEl) {
            const allTooltips = document.querySelectorAll('[id^="chart-"][id$="-tooltip"]');
            allTooltips.forEach(tt => {
                if (tt.id !== currentTooltipEl.id) {
                    tt.style.opacity = 0;
                    tt.style.pointerEvents = 'none';
                }
            });
        },

        /**
         * Hide all chart tooltips (e.g. when this chart's callback runs but we are not showing a tooltip)
         */
        hideAllChartTooltips() {
            const allTooltips = document.querySelectorAll('[id^="chart-"][id$="-tooltip"]');
            allTooltips.forEach(tt => {
                tt.style.opacity = 0;
                tt.style.pointerEvents = 'none';
            });
        },

        /**
         * Remove the tooltip element from the DOM
         * This should be called when the component is destroyed
         */
        cleanupChartTooltip() {
            const chartId = this.id;
            if (chartId == null) {
                return;
            }

            const tooltipPrefix = `chart-${chartId}-`;
            document.querySelectorAll(`[id^="${tooltipPrefix}"][id$="-tooltip"]`).forEach((tooltipEl) => {
                tooltipEl.remove();
            });
        },

        /**
         * Safely create a Chart.js instance once the canvas element becomes visible
         * This avoids the incorrect animation baseline issue that occurs when Chart.js
         * initialises while its container is hidden (e.g. inside a v-show tab)
         *
         * @param {HTMLCanvasElement} canvas - The canvas element for the chart
         * @param {Object} config - The Chart.js configuration object
         * @param {Function} [callback] - Optional callback invoked after the chart is created
         */
        createChartWhenVisible(canvas, config, callback = () => { }) {
            if (!canvas) {
                console.warn('Chart canvas not found');
                return null;
            }

            // Expose the config early so export routines can still access it even if the
            // actual Chart instance hasn't been created yet.
            canvas.__chartConfigExport = config;

            const isVisible = (el) => {
                // An element is considered visible if it has a layout box (offsetParent)
                // and its dimensions are non-zero.
                return el.offsetParent !== null && el.offsetHeight !== 0 && el.offsetWidth !== 0;
            };

            const tryCreate = () => {
                if (!isVisible(canvas)) {
                    // Element still hidden – try again on next animation frame
                    requestAnimationFrame(tryCreate);
                    return;
                }
                // At this point the element is visible. Safe to create / recreate the chart.
                try {
                    const existing = Chart.getChart(canvas);
                    if (existing) {
                        // Ensure any running animations are halted before destroying to avoid
                        // race-conditions that can invoke draw calls on a nulled canvas.
                        try {
                            if (typeof existing.stop === 'function') {
                                existing.stop();
                            }
                        } catch (_) { }
                        existing.destroy();
                    }
                    // Create chart and keep a reference of the config on the canvas element so that
                    // export routines can still rebuild the chart later even if it was never rendered
                    const chartInstance = new Chart(canvas, config);
                    // Store raw config for potential SVG export use (cannot store functions via dataset)
                    canvas.__chartConfigExport = config;
                    callback(chartInstance);
                } catch (err) {
                    console.error('Error creating chart:', err);
                }
            };

            // Kick off the visibility polling
            tryCreate();
        }
    },
    beforeUnmount() {
        // Clean up any tooltips created by this chart
        this.cleanupChartTooltip();
        // Clear any pending timeouts
        if (this.tooltipHideTimeout) {
            clearTimeout(this.tooltipHideTimeout);
            this.tooltipHideTimeout = null;
        }
        if (this.tooltipShowTimeout) {
            clearTimeout(this.tooltipShowTimeout);
            this.tooltipShowTimeout = null;
        }
    }
}

// --- GLOBAL SAFETY PATCH ----------------------------------------------------
// In some edge-cases Chart.js may attempt to run draw/animation frames after a
// chart instance has been destroyed, resulting in errors such as:
// "Cannot read properties of null (reading 'getContext')".
// To mitigate this, we monkey-patch the default destroy method once at run-time
// to explicitly stop any running animations before the internal destroy logic
// executes. This patch is applied only once and is completely backward compatible.

if (typeof window !== 'undefined' && window.Chart && !window.Chart.__safeDestroyPatched) {
    const ChartJS = window.Chart;
    const originalDestroy = ChartJS.prototype.destroy;

    ChartJS.prototype.destroy = function (...args) {
        try {
            // Halt active animations to prevent stray draw calls after destroy
            if (typeof this.stop === 'function') {
                this.stop();
            }
        } catch (_) {
            // Swallow any errors silently – destruction must always continue
        }

        return originalDestroy.apply(this, args);
    };

    // Flag so we don't patch multiple times in HMR / dev environments
    ChartJS.__safeDestroyPatched = true;
}
// ---------------------------------------------------------------------------

// Additional safety patch: Guard against null canvas/context in helpers.clearCanvas
// ---------------------------------------------------------------------------
// In some rare scenarios a destroy/DOM update race-condition can result in Chart.js
// attempting to clear a canvas element that has already been removed which leads to
// runtime errors such as "Cannot read properties of null (reading 'getContext')".
// To harden the app, we wrap the internal clearCanvas helper so it simply bails out
// when either the canvas or its 2D context is missing. This patch is applied only
// once to avoid double-wrapping during HMR.

if (typeof window !== 'undefined' && window.Chart) {
    const ChartJS = window.Chart;

    // Chart.js v3/v4 expose helper utilities under ChartJS.helpers or ChartJS.helpers.canvas
    const helpers = ChartJS.helpers || {};
    const canvasHelpers = helpers.canvas || {};

    if (typeof canvasHelpers.clearCanvas === 'function' && !canvasHelpers.__safePatched) {
        const originalClearCanvas = canvasHelpers.clearCanvas;

        canvasHelpers.clearCanvas = function (canvas, ctx) {
            // Bail early if either argument is missing – avoids "getContext" errors
            if (!canvas || !ctx) {
                return;
            }
            return originalClearCanvas.apply(this, arguments);
        };

        // Mark so we don't patch multiple times in dev/HMR environments
        canvasHelpers.__safePatched = true;
    }
}
// ---------------------------------------------------------------------------

// Extra guard: abort Chart.clear if the chart no longer has a canvas/ctx
if (typeof window !== 'undefined' && window.Chart && !window.Chart.__safeClearPatched) {
    const ChartJS = window.Chart;
    const originalClear = ChartJS.prototype.clear;

    ChartJS.prototype.clear = function (...args) {
        if (!this?.canvas || !this?.ctx) {
            // canvas has been detached/destroyed; skip clearing to avoid crashes
            return this;
        }
        return originalClear.apply(this, args);
    };

    ChartJS.__safeClearPatched = true;
}
// ---------------------------------------------------------------------------

// Fallback guard: abort Chart.draw when context is missing (prevents clipArea crashes)
// ---------------------------------------------------------------------------
// In some rare scenarios a destroy/DOM update race-condition can result in Chart.js
// attempting to clear a canvas element that has already been removed which leads to
// runtime errors such as "Cannot read properties of null (reading 'getContext')".
// To harden the app, we wrap the internal clearCanvas helper so it simply bails out
// when either the canvas or its 2D context is missing. This patch is applied only
// once to avoid double-wrapping during HMR.

if (typeof window !== 'undefined' && window.Chart && !window.Chart.__safeDrawPatched) {
    const ChartJS = window.Chart;
    const originalDraw = ChartJS.prototype.draw;

    ChartJS.prototype.draw = function (...args) {
        if (!this?.ctx) {
            return this;
        }
        try {
            return originalDraw.apply(this, args);
        } catch (error) {
            // Check if this error is specifically about SVG NaN attributes to avoid flooding console
            if (error?.message?.includes('Expected number') || error?.message?.includes('Expected length')) {
                return this;
            }
            console.error('Error during chart draw:', error);
            return this;
        }
    };

    ChartJS.__safeDrawPatched = true;
}
// ---------------------------------------------------------------------------

// Safety patch: Guard against null animator in _updateVisibility (prevents legend click crashes)
// ---------------------------------------------------------------------------
// When clicking legend items, Chart.js calls show/hide which invokes _updateVisibility.
// If the chart's animator is null (e.g., during destroy or initialization), this causes
// "Cannot read properties of null (reading '_resolveAnimations')" errors.
if (typeof window !== 'undefined' && window.Chart && !window.Chart.__safeUpdateVisibilityPatched) {
    const ChartJS = window.Chart;

    // Patch _updateVisibility if it exists - wrap in try-catch to handle null animator
    if (ChartJS.prototype._updateVisibility) {
        const originalUpdateVisibility = ChartJS.prototype._updateVisibility;

        ChartJS.prototype._updateVisibility = function (...args) {
            // Check if chart is in a valid state before proceeding
            if (!this?.canvas || !this?.ctx) {
                return this;
            }

            // Wrap in try-catch to handle null animator gracefully
            try {
                return originalUpdateVisibility.apply(this, args);
            } catch (error) {
                // If error is related to null animator, silently fail
                if (error?.message?.includes('_resolveAnimations') ||
                    error?.message?.includes('Cannot read properties of null')) {
                    return this;
                }
                // Re-throw other errors
                throw error;
            }
        };
    }

    // Also patch show and hide methods as they call _updateVisibility
    if (ChartJS.prototype.show) {
        const originalShow = ChartJS.prototype.show;
        ChartJS.prototype.show = function (...args) {
            if (!this?.canvas || !this?.ctx) {
                return this;
            }
            try {
                return originalShow.apply(this, args);
            } catch (error) {
                if (error?.message?.includes('_resolveAnimations') ||
                    error?.message?.includes('Cannot read properties of null')) {
                    return this;
                }
                throw error;
            }
        };
    }

    if (ChartJS.prototype.hide) {
        const originalHide = ChartJS.prototype.hide;
        ChartJS.prototype.hide = function (...args) {
            if (!this?.canvas || !this?.ctx) {
                return this;
            }
            try {
                return originalHide.apply(this, args);
            } catch (error) {
                if (error?.message?.includes('_resolveAnimations') ||
                    error?.message?.includes('Cannot read properties of null')) {
                    return this;
                }
                throw error;
            }
        };
    }

    ChartJS.__safeUpdateVisibilityPatched = true;
}
// ---------------------------------------------------------------------------
